import { Megaphone } from "lucide-react";
import { useGetList, useRecordContext, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

import { FilterCategory } from "../filters/FilterCategory";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";

// How the buyer found us (Facebook group, Marketplace, TikTok, Whatnot...).
// The list of channels is edited in Settings > Contacts.

export const LeadSourceInput = () => {
  const { leadSources } = useConfigurationContext();
  return (
    <SelectInput
      source="lead_source"
      label="egi.lead_source.label"
      choices={leadSources}
      optionValue="value"
      optionText="label"
      helperText={false}
    />
  );
};

// Free text so a new group can be typed straight away; the browser's datalist suggests the
// details already used (same channel first) so one group is not spelled several ways.
const DETAIL_LIST_ID = "lead-source-detail-suggestions";
export const LeadSourceDetailInput = () => {
  const leadSource = useWatch({ name: "lead_source" });
  // ponytail: pulls every contact that has a detail; fine for a few thousand contacts.
  const { data = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "id", order: "DESC" },
    filter: { "lead_source_detail@neq": "" },
  });
  const sameChannel = new Set<string>();
  const others = new Set<string>();
  for (const c of data) {
    const detail = c.lead_source_detail?.trim();
    if (!detail) continue;
    (leadSource && c.lead_source === leadSource ? sameChannel : others).add(
      detail,
    );
  }
  const suggestions = [
    ...sameChannel,
    ...[...others].filter((d) => !sameChannel.has(d)),
  ];
  return (
    <>
      <TextInput
        source="lead_source_detail"
        label="egi.lead_source.detail"
        helperText="egi.lead_source.detail_help"
        list={DETAIL_LIST_ID}
        autoComplete="off"
      />
      <datalist id={DETAIL_LIST_ID}>
        {suggestions.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>
    </>
  );
};

// A value that was removed from Settings still shows as stored, so no data silently disappears.
const useLeadSourceLabel = (value?: string | null) => {
  const { leadSources } = useConfigurationContext();
  if (!value) return null;
  return leadSources.find((s) => s.value === value)?.label ?? value;
};

export const LeadSourceBadge = () => {
  const record = useRecordContext<Contact>();
  const label = useLeadSourceLabel(record?.lead_source);
  if (!label) return null;
  return (
    <Badge variant="outline" className="font-normal">
      {label}
    </Badge>
  );
};

export const LeadSourceAsideValue = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  const label = useLeadSourceLabel(record?.lead_source);
  const detail = record?.lead_source_detail?.trim();
  return (
    <div className="flex flex-col items-start gap-1">
      {label ? (
        <LeadSourceBadge />
      ) : (
        <span className="text-muted-foreground">
          {translate("egi.lead_source.unknown")}
        </span>
      )}
      {detail ? (
        <span>
          <span className="text-muted-foreground">
            {translate("egi.lead_source.detail")}:{" "}
          </span>
          {detail}
        </span>
      ) : null}
    </div>
  );
};

export const LeadSourceFilter = () => {
  const { leadSources } = useConfigurationContext();
  const isMobile = useIsMobile();
  return (
    <FilterCategory label="egi.lead_source.label" icon={<Megaphone />}>
      {leadSources.map((source) => (
        <ToggleFilterButton
          key={source.value}
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={source.label}
          value={{ lead_source: source.value }}
          size={isMobile ? "lg" : undefined}
        />
      ))}
      <ToggleFilterButton
        className="w-auto md:w-full justify-between h-10 md:h-8"
        label="egi.lead_source.unknown"
        // a null filter value is dropped by ra-core; the string becomes PostgREST "is.null"
        value={{ "lead_source@is": "null" }}
        size={isMobile ? "lg" : undefined}
      />
    </FilterCategory>
  );
};
